/**
 * Rotating "Hosts already listing in…" city strip (Iter 67j).
 *
 * Lightweight credibility ribbon for the BecomeVendor hero. Cycles through the
 * real markets RentalCar.al serves so prospective vendors instantly see we're
 * already active in their region. Crossfade-and-slide animation between cities;
 * a tiny dot rail below shows progress.
 *
 * No external libs — pure React state + Tailwind transitions. Cycles every
 * ~2.4s, pausable on hover so users can read all cities.
 */
import { useEffect, useState } from "react";
import { MapPin, Sparkles } from "lucide-react";

// Iter 67en+++ (Feb 2026) — Refocused to the actual Albanian markets
// RentalCar.al covers. Replaced the older multi-Balkan rotation that
// made the platform look unfocused; flag is fixed to 🇦🇱 because every
// destination is in Albania.
const CITIES = [
  { city: "Tirana Airport", country: "Albania", flag: "🇦🇱" },
  { city: "Saranda",        country: "Albania", flag: "🇦🇱" },
  { city: "Berat",          country: "Albania", flag: "🇦🇱" },
  { city: "Gjirokastër",    country: "Albania", flag: "🇦🇱" },
  { city: "Durrës",         country: "Albania", flag: "🇦🇱" },
  { city: "Elbasan",        country: "Albania", flag: "🇦🇱" },
  { city: "Shkodër",        country: "Albania", flag: "🇦🇱" },
];

const ROTATE_MS = 2400;

export default function VendorCityStrip() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % CITIES.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused]);

  const current = CITIES[idx];

  return (
    <div
      className="mt-12 mx-auto max-w-xl"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="vendor-city-strip"
    >
      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-r from-sky-50/70 via-white to-violet-50/70 px-5 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-indigo-100 text-[10px] font-bold uppercase tracking-wider text-indigo-700 shrink-0">
            <Sparkles className="h-3 w-3 text-amber-500" /> Live
          </div>
          <div className="text-sm text-neutral-600 shrink-0">Hosts already listing in</div>
          <div className="relative h-6 overflow-hidden flex-1 min-w-0">
            {CITIES.map((c, i) => (
              <div
                key={`${c.city}-${c.country}`}
                className={`absolute inset-0 inline-flex items-center gap-1.5 transition-all duration-500 ease-out ${
                  i === idx
                    ? "translate-y-0 opacity-100"
                    : i === (idx - 1 + CITIES.length) % CITIES.length
                    ? "-translate-y-full opacity-0"
                    : "translate-y-full opacity-0"
                }`}
                aria-hidden={i !== idx}
              >
                <span className="text-base leading-none" aria-hidden="true">{c.flag}</span>
                <MapPin className="h-3.5 w-3.5 text-violet-500 shrink-0" />
                <span className="text-sm font-semibold text-neutral-900 truncate">
                  {c.city}
                  <span className="text-neutral-500 font-normal"> · {c.country}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
        {/* Progress rail — tiny dots, current one widens. */}
        <div className="mt-2.5 flex items-center justify-center gap-1.5" data-testid="vendor-city-strip-rail">
          {CITIES.map((_, i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === idx ? "w-5 bg-indigo-500" : "w-1.5 bg-neutral-300"
              }`}
            />
          ))}
        </div>
      </div>
      <div className="mt-2 text-center text-[11px] text-neutral-400">
        Live snapshot · {CITIES.length} markets served and growing.
      </div>
    </div>
  );
}
